import { MailSearch, SearchX } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { ListToolbar } from '@/components/list/ListToolbar';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { readErrorKey } from '@/lib/read-error';
import { DownloadButton } from './DownloadButton';
import { LogCards, LogTable } from './LogLists';
import { useLog } from './use-log';

/** What sits under the toolbar: loading, failed, empty, no results, or the rows and «Mostra altri». */
function LogBody({ view }: { readonly view: ReturnType<typeof useLog> }) {
  const { t } = useTranslation(['communications', 'errors']);
  const { state, shown } = view;
  return (
    <>
      {state.status === 'loading' ? (
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-12 w-full" />
          ))}
        </SkeletonRegion>
      ) : null}
      {state.status === 'error' ? (
        <StateMessage
          role="alert"
          tone="error"
          icon={MailSearch}
          title={t(readErrorKey(state.httpStatus))}
          action={
            <Button variant="outline" onClick={view.reload}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : null}
      {state.status === 'ready' && shown.length === 0 && view.narrowed ? (
        <StateMessage
          icon={SearchX}
          title={t('log.noResults')}
          action={
            <Button variant="outline" onClick={view.clear}>
              {t('log.clear')}
            </Button>
          }
        />
      ) : null}
      {state.status === 'ready' && shown.length === 0 && !view.narrowed ? (
        <StateMessage icon={MailSearch} title={t('log.empty')} text={t('log.emptyText')} />
      ) : null}
      {state.status === 'ready' && shown.length > 0 ? (
        <>
          <LogTable items={shown} />
          <LogCards items={shown} />
          {state.moreFailed ? (
            <p role="alert" className="text-destructive text-sm">
              {t('common.failed')}
            </p>
          ) : null}
          {state.nextCursor !== null ? (
            <Button
              variant="outline"
              className="min-h-11 self-center"
              disabled={state.loadingMore}
              onClick={view.loadMore}
            >
              {t('log.more')}
            </Button>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/** «Comunicazioni → Registro» (spec §5.3): every onboarding message and every choice, filterable. */
export function LogPage() {
  const { t } = useTranslation('communications');
  const titleId = useId();
  const view = useLog();
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <PageHead
        titleId={titleId}
        title={t('log.title')}
        summary={t('log.summary')}
        actions={<DownloadButton filters={view.filters} />}
      />
      <ListToolbar
        label={t('log.title')}
        search={{ label: t('log.search'), value: view.q, onWrite: (v) => view.set('q', v) }}
        fields={view.fields}
        active={view.active}
        onFilter={view.set}
        onClear={view.clear}
      />
      <LogBody view={view} />
    </section>
  );
}
